/**
 * TraceabilityCoverage - Metricas de Cobertura de Trazabilidad
 * PECAL 2210 - Analisis de Trazabilidad Bidireccional
 */

import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  GitBranch,
  ArrowUpRight,
  ArrowDownRight,
  TestTube,
  Layers,
  AlertTriangle,
  CheckCircle2,
  Info,
} from 'lucide-react';
import { useTraceabilityCoverage } from '@/hooks/useRequirements';
import { cn } from '@/lib/utils';
import { levelLabels, levelColors } from '@/types/requirements';

interface TraceabilityCoverageProps {
  className?: string;
  compact?: boolean;
}

export function TraceabilityCoverage({ className, compact = false }: TraceabilityCoverageProps) {
  const { data: coverage, isLoading } = useTraceabilityCoverage();

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center h-32">
          <span className="text-muted-foreground">Cargando...</span>
        </CardContent>
      </Card>
    );
  }

  if (!coverage) {
    return null;
  }

  const getCoverageColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCoverageProgressColor = (percentage: number) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (compact) {
    return (
      <Card className={className}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Cobertura Trazabilidad</span>
            <span className={cn('text-2xl font-bold', getCoverageColor(coverage.coverage_percentage))}>
              {coverage.coverage_percentage?.toFixed(0) || 0}%
            </span>
          </div>
          <Progress
            value={coverage.coverage_percentage || 0}
            className="h-2"
          />
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>{coverage.fully_traced} trazados</span>
            <span>{coverage.total_requirements} total</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitBranch className="w-5 h-5" />
          Cobertura de Trazabilidad
        </CardTitle>
        <CardDescription>Analisis PECAL 2210</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Cobertura Global</p>
            <p className={cn('text-4xl font-bold', getCoverageColor(coverage.coverage_percentage))}>
              {coverage.coverage_percentage?.toFixed(1) || 0}%
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Total Requisitos</p>
            <p className="text-2xl font-semibold">{coverage.total_requirements}</p>
          </div>
        </div>

        <Progress
          value={coverage.coverage_percentage || 0}
          className="h-3"
        />

        <div className="grid grid-cols-2 gap-4">
          <MetricCard
            icon={ArrowUpRight}
            label="Con Padre"
            value={coverage.with_parent_link}
            total={coverage.total_requirements}
            description="Requisitos con trazabilidad hacia arriba"
          />
          <MetricCard
            icon={ArrowDownRight}
            label="Con Hijos"
            value={coverage.with_children}
            total={coverage.total_requirements}
            description="Requisitos con derivaciones"
          />
          <MetricCard
            icon={TestTube}
            label="Con Pruebas"
            value={coverage.with_test_coverage}
            total={coverage.total_requirements}
            description="Requisitos vinculados a casos de prueba"
          />
          <MetricCard
            icon={Layers}
            label="Con Diseno"
            value={coverage.with_design_link}
            total={coverage.total_requirements}
            description="Requisitos vinculados a elementos de diseno"
          />
        </div>

        {coverage.orphan_requirements > 0 && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <div>
              <p className="font-medium text-yellow-800">
                {coverage.orphan_requirements} requisitos huerfanos
              </p>
              <p className="text-xs text-yellow-700">
                Sin trazabilidad hacia requisitos padre
              </p>
            </div>
          </div>
        )}

        {coverage.by_level && Object.keys(coverage.by_level).length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium flex items-center gap-1">
              Por Nivel
              <Tooltip>
                <TooltipTrigger>
                  <Info className="w-3 h-3 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  Cobertura de trazabilidad por nivel ISO 29148
                </TooltipContent>
              </Tooltip>
            </h4>
            <div className="space-y-2">
              {Object.entries(coverage.by_level).map(([level, data]) => (
                <div key={level} className="flex items-center gap-3">
                  <Badge className={cn(levelColors[level as keyof typeof levelColors], 'w-16 justify-center')}>
                    {levelLabels[level as keyof typeof levelLabels] || level}
                  </Badge>
                  <div className="flex-1">
                    <Progress value={data.percentage || 0} className="h-2" />
                  </div>
                  <span className="text-sm w-16 text-right">
                    {data.traced}/{data.total}
                  </span>
                  <span
                    className={cn(
                      'text-sm font-medium w-12 text-right',
                      getCoverageColor(data.percentage || 0)
                    )}
                  >
                    {data.percentage?.toFixed(0) || 0}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {coverage.by_category && Object.keys(coverage.by_category).length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Cobertura de Pruebas por Categoria</h4>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(coverage.by_category).map(([category, data]) => {
                const percentage = data.total > 0 ? (data.with_tests / data.total) * 100 : 0;
                return (
                  <div
                    key={category}
                    className="flex items-center justify-between p-2 rounded bg-muted/50"
                  >
                    <span className="text-sm capitalize">{category.replace('_', ' ')}</span>
                    <span className={cn('text-sm font-medium', getCoverageColor(percentage))}>
                      {data.with_tests}/{data.total}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  total,
  description,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  total: number;
  description: string;
}) {
  const percentage = total > 0 ? (value / total) * 100 : 0;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-default">
          <div
            className={cn(
              'p-2 rounded-full',
              percentage >= 80
                ? 'bg-green-100 text-green-600'
                : percentage >= 50
                ? 'bg-yellow-100 text-yellow-600'
                : 'bg-red-100 text-red-600'
            )}
          >
            <Icon className="w-4 h-4" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="font-semibold">
              {value}
              <span className="text-muted-foreground font-normal text-sm">/{total}</span>
            </p>
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>{description}</p>
        <p className="text-xs text-muted-foreground mt-1">
          {percentage.toFixed(1)}% del total
        </p>
      </TooltipContent>
    </Tooltip>
  );
}

export function TraceabilityValidationCard({
  requirementId,
  className,
}: {
  requirementId: string;
  className?: string;
}) {
  const { useTraceabilityValidation } = require('@/hooks/useRequirements');
  const { data: validation, isLoading } = useTraceabilityValidation(requirementId);

  if (isLoading || !validation) {
    return null;
  }

  const scoreColor =
    validation.traceability_score >= 80
      ? 'text-green-600 bg-green-100'
      : validation.traceability_score >= 50
      ? 'text-yellow-600 bg-yellow-100'
      : 'text-red-600 bg-red-100';

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Validacion de Trazabilidad</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Score</span>
          <Badge className={scoreColor}>{validation.traceability_score}/100</Badge>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-1">
            {validation.has_parent ? (
              <CheckCircle2 className="w-3 h-3 text-green-600" />
            ) : (
              <AlertTriangle className="w-3 h-3 text-yellow-600" />
            )}
            <span>Padre</span>
          </div>
          <div className="flex items-center gap-1">
            {validation.has_children ? (
              <CheckCircle2 className="w-3 h-3 text-green-600" />
            ) : (
              <AlertTriangle className="w-3 h-3 text-yellow-600" />
            )}
            <span>Hijos</span>
          </div>
          <div className="flex items-center gap-1">
            {validation.has_test_link ? (
              <CheckCircle2 className="w-3 h-3 text-green-600" />
            ) : (
              <AlertTriangle className="w-3 h-3 text-red-600" />
            )}
            <span>Pruebas</span>
          </div>
          <div className="flex items-center gap-1">
            {validation.has_design_link ? (
              <CheckCircle2 className="w-3 h-3 text-green-600" />
            ) : (
              <AlertTriangle className="w-3 h-3 text-yellow-600" />
            )}
            <span>Diseno</span>
          </div>
        </div>

        {validation.issues.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-red-600">Problemas:</p>
            <ul className="text-xs text-muted-foreground list-disc pl-4">
              {validation.issues.map((issue, idx) => (
                <li key={idx}>{issue}</li>
              ))}
            </ul>
          </div>
        )}

        {validation.recommendations.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-blue-600">Recomendaciones:</p>
            <ul className="text-xs text-muted-foreground list-disc pl-4">
              {validation.recommendations.map((rec, idx) => (
                <li key={idx}>{rec}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
